// Captures the tab from the browser's own pixels (getDisplayMedia) instead of re-rendering the DOM,
// so the cost stays the same however many elements the page has.

export interface IViewportFrame {
  canvas: HTMLCanvasElement
  // Frame pixels per CSS pixel.
  scale: number
}

export interface ITabStream {
  video: HTMLVideoElement
  stop: () => void
}

// Chrome-only hints: offer this tab first in the picker. Other browsers ignore them.
type IDisplayMediaOptions = DisplayMediaStreamOptions & {
  preferCurrentTab?: boolean
  selfBrowserSurface?: 'include' | 'exclude'
}

// Hiding the widget has to be painted and reach the capture stream before a frame is taken,
// or the overlay ends up in the screenshot.
const FRAME_SETTLE_MS = 300

// How far the frame's aspect may drift from the viewport's before it is taken to be another
// surface (a different window or screen picked in the browser's dialog).
const MAX_ASPECT_DRIFT = 0.02

// Firefox and Safari share only windows and screens, whose frames are never this tab's viewport.
// CropTarget (Region Capture) exists only where the current tab itself can be shared.
export const isTabCaptureSupported = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
  'CropTarget' in window

export const openTabStream = async (): Promise<ITabStream> => {
  const options: IDisplayMediaOptions = {
    video: { displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
  }
  const stream = await navigator.mediaDevices.getDisplayMedia(options)
  const stop = () => stream.getTracks().forEach((track) => track.stop())
  try {
    const video = document.createElement('video')
    video.muted = true
    video.srcObject = stream
    await video.play()
    return { video, stop }
  } catch (error) {
    stop()
    throw error
  }
}

export const waitForFreshFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, FRAME_SETTLE_MS)))
  })

// Null when the frame is empty or is not this tab's viewport. Drawn down to at most `maxPixels`,
// since a high-DPI frame is otherwise larger than a screenshot may be.
export const grabViewportFrame = (
  video: HTMLVideoElement,
  maxPixels: number,
): IViewportFrame | null => {
  const { videoWidth, videoHeight } = video
  if (!videoWidth || !videoHeight) {
    return null
  }
  const frameAspect = videoWidth / videoHeight
  const viewportAspect = window.innerWidth / window.innerHeight
  if (Math.abs(frameAspect / viewportAspect - 1) > MAX_ASPECT_DRIFT) {
    return null
  }
  const fit = Math.min(1, Math.sqrt(maxPixels / (videoWidth * videoHeight)))
  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(videoWidth * fit)
  canvas.height = Math.floor(videoHeight * fit)
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  return { canvas, scale: canvas.width / window.innerWidth }
}
